/**
 * SearchPortalConfig — pannello admin per configurare le API key di ricerca mercato
 *
 * Le chiavi vengono salvate nel file .env del backend tramite l'endpoint
 * POST /api/v1/ai-settings/procurement-keys
 */
import { useState, useEffect } from 'react'
import {
  Form, Input, Button, Alert, Divider, Tag, Typography, Space, Spin,
  Tooltip, theme,
} from 'antd'
import {
  SaveOutlined, EyeOutlined, EyeInvisibleOutlined, CheckCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import axiosInstance from '@/lib/axios'

const { Text, Title, Paragraph } = Typography

interface KeyField {
  name: string
  label: string
  envKey: string
  placeholder: string
  helpUrl: string
  helpText: string
  required: boolean
}

const KEY_FIELDS: KeyField[] = [
  {
    name: 'serpapi_key',
    label: 'SerpAPI Key',
    envKey: 'SERPAPI_KEY',
    placeholder: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx...',
    helpUrl: 'https://serpapi.com/manage-api-key',
    helpText: 'Abilita Google Shopping e Amazon. Piano gratuito: 100 ricerche/mese.',
    required: false,
  },
  {
    name: 'tavily_key',
    label: 'Tavily API Key',
    envKey: 'TAVILY_API_KEY',
    placeholder: 'tvly-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    helpUrl: 'https://app.tavily.com/home',
    helpText: 'Ricerca web AI. Piano gratuito: 1.000 ricerche/mese.',
    required: false,
  },
  {
    name: 'ebay_app_id',
    label: 'eBay App ID',
    envKey: 'EBAY_APP_ID',
    placeholder: 'NomeSoci-GarageMA-PRD-xxxxxxxxx-xxxxxxxx',
    helpUrl: 'https://developer.ebay.com/my/keys',
    helpText: 'eBay Finding API. Gratuito con account sviluppatore.',
    required: false,
  },
]

export function SearchPortalConfig() {
  const { token } = theme.useToken()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentKeys, setCurrentKeys] = useState<Record<string, string>>({})
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({})

  useEffect(() => {
    fetchCurrentConfig()
  }, [])

  const fetchCurrentConfig = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance.get('/ai-settings/procurement-keys')
      setCurrentKeys(res.data)
    } catch {
      // endpoint potrebbe non esistere ancora
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (values: Record<string, string>) => {
    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      await axiosInstance.post('/ai-settings/procurement-keys', values)
      form.resetFields()
      setSuccess(true)
      fetchCurrentConfig()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Spin />

  return (
    <div style={{ maxWidth: 600 }}>
      <Title level={5} style={{ marginTop: 0 }}>
        Configurazione Portali di Ricerca
      </Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Configura le API key per abilitare la ricerca automatica di offerte di acquisto
        ricambi da più fonti. Tutte le chiavi sono opzionali — il sistema funziona
        anche con un sottoinsieme di fonti configurate.
        Claude (Anthropic) è sempre usato per il ranking AI — configuralo nel tab
        "Intelligenza Artificiale".
      </Paragraph>

      {success && (
        <Alert
          type="success"
          icon={<CheckCircleOutlined />}
          message="Configurazione salvata con successo"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setSuccess(false)}
        />
      )}
      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} closable onClose={() => setError(null)} />
      )}

      <Form form={form} layout="vertical" onFinish={handleSave}>
        {KEY_FIELDS.map(field => {
          const currentVal = currentKeys[field.envKey]
          const isConfigured = currentVal && currentVal !== 'your-' + field.name
          return (
            <div key={field.name}>
              <Form.Item
                name={field.name}
                label={
                  <Space>
                    <Text strong>{field.label}</Text>
                    {isConfigured
                      ? <Tag color="success">Configurata</Tag>
                      : <Tag color="default">Non configurata</Tag>
                    }
                    <Tooltip title={field.helpText}>
                      <QuestionCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </Space>
                }
                extra={
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {field.helpText}{' '}
                    <a href={field.helpUrl} target="_blank" rel="noopener noreferrer">
                      Ottieni chiave →
                    </a>
                  </Text>
                }
              >
                <Input.Password
                  placeholder={isConfigured ? '(chiave già configurata — lascia vuoto per non cambiare)' : field.placeholder}
                  visibilityToggle={{
                    visible: showKeys[field.name],
                    onVisibleChange: (v) => setShowKeys(prev => ({ ...prev, [field.name]: v })),
                  }}
                  iconRender={(visible) =>
                    visible ? <EyeInvisibleOutlined /> : <EyeOutlined />
                  }
                />
              </Form.Item>
            </div>
          )
        })}

        <Divider />
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            icon={<SaveOutlined />}
          >
            Salva configurazione
          </Button>
        </Form.Item>
      </Form>

      <Divider />
      <Title level={5}>Fonti disponibili</Title>
      <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr style={{ background: token.colorFillAlter }}>
            <th style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #f0f0f0' }}>Fonte</th>
            <th style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #f0f0f0' }}>Tipo</th>
            <th style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #f0f0f0' }}>Piano gratuito</th>
            <th style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #f0f0f0' }}>Stato</th>
          </tr>
        </thead>
        <tbody>
          {[
            { name: 'Google Shopping', key: 'SERPAPI_KEY', type: 'SerpAPI', free: '100/mese' },
            { name: 'Amazon.it', key: 'SERPAPI_KEY', type: 'SerpAPI', free: '100/mese' },
            { name: 'eBay', key: 'EBAY_APP_ID', type: 'eBay Finding API', free: 'Illimitato' },
            { name: 'Ricerca Web AI', key: 'TAVILY_API_KEY', type: 'Tavily', free: '1.000/mese' },
            { name: 'Ranking AI', key: 'ANTHROPIC', type: 'Anthropic Claude', free: 'A consumo' },
          ].map(row => (
            <tr key={row.name}>
              <td style={{ padding: '5px 10px', border: '1px solid #f0f0f0' }}>{row.name}</td>
              <td style={{ padding: '5px 10px', border: `1px solid ${token.colorBorderSecondary}`, color: token.colorTextSecondary }}>{row.type}</td>
              <td style={{ padding: '5px 10px', border: `1px solid ${token.colorBorderSecondary}`, color: token.colorTextSecondary }}>{row.free}</td>
              <td style={{ padding: '5px 10px', border: '1px solid #f0f0f0' }}>
                {currentKeys[row.key]
                  ? <Tag color="success">✓ Attiva</Tag>
                  : <Tag color="default">Non configurata</Tag>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
